import { OFFERS_PREVIEW_BUILD } from './offer-env'
export const NETWORK = process.env.NEXT_PUBLIC_NETWORK || 'BTC'

// natcats-renderer in our cluster: share cards, coin prices and page
// screenshots. It replaced the Vercel cloud-functions project at the same
// paths, so only the host changed.
export const RENDERER_URL =
	process.env.NEXT_PUBLIC_RENDERER_URL || 'https://renderer.ordinalswallet.com'

export const NETWORK_CONFIG = {
	BTC: {
		name: 'Bitcoin',
		brc20Protocol: 'brc-20',
		networkParams: undefined,
		derivation: "m/86'/0'/0'/0",
		postage: 546,
		maxPostage: 20000,
		exchangeRateUrl: `${RENDERER_URL}/api/btc-exchange-rate`
	},

	BELLS: {
		name: 'Bells',
		brc20Protocol: 'bel-20',
		networkParams: {
			bech32: 'bel',
			pubKeyHash: 25,
			scriptHash: 30,
			wif: 0x99
		},
		derivation: "m/44'/3'/0'/0/0",
		postage: 100000,
		maxPostage: 500000,
		exchangeRateUrl: `${RENDERER_URL}/api/bells-exchange-rate`
	},
	DOGE: {
		name: 'Doge',
		brc20Protocol: 'drc-20',
		networkParams: {
			bech32: 'bc',
			pubKeyHash: 0x1e,
			scriptHash: 0x16,
			wif: 0x9e
		},
		derivation: "m/44'/3'/0'/0/0",
		postage: 100000,
		maxPostage: 500000,
		exchangeRateUrl: `${RENDERER_URL}/api/doge-exchange-rate`
	},
	SIGNET: {
		name: 'Signet',
		brc20Protocol: 'brc-20',
		networkParams: {
			bech32: 'tb',
			pubKeyHash: 0x6f,
			scriptHash: 0xc4,
			wif: 0xef
		},
		derivation: "m/86'/0'/0'/0",
		postage: 546,
		maxPostage: 20000
	}
}[NETWORK]

export const NETWORK_NAME = (NETWORK_CONFIG as any).name
export const NETWORK_FUNGIBLE_PROTOCOL = (NETWORK_CONFIG as any).brc20Protocol
export const FEATURE_FLAGS = process.env.NEXT_PUBLIC_FEATURES
	? process.env.NEXT_PUBLIC_FEATURES.split(',')
	: []
export const SELF_URL = 'https://ordinalswallet.com'
// Public prod endpoints as code fallbacks: NEXT_PUBLIC_* values are baked
// into the client bundle anyway, and a git-triggered preview build has no
// Preview env — without fallbacks it shipped an app with `undefined` for
// every API base, which is why deploys regressed to CLI --build-env.
export const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://turbo.ordinalswallet.com'
// Preview builds may point offers at an isolated API. Every other build
// ignores that override (fail-closed: an unset NEXT_PUBLIC_VERCEL_ENV is not a
// preview): funded escrows live in one database and can never be moved, so
// production offers always go to the main API.
const OFFERS_API_OVERRIDE = OFFERS_PREVIEW_BUILD ? process.env.NEXT_PUBLIC_OFFERS_API_URL : undefined
export const OFFERS_API_URL = OFFERS_API_OVERRIDE || API_URL
export const INSCRIBE_API_URL = process.env.NEXT_PUBLIC_INSCRIBE_API_URL || API_URL
export const API_CLOUD_URL = process.env.CLOUD_API_URL
export const API_MINT_URL = process.env.NEXT_PUBLIC_MINT_URL || 'https://mint.ordinalswallet.com'
export const TAP_READER_URL =
	process.env.NEXT_PUBLIC_TAP_READER_URL || 'https://tap-reader.ordinalswallet.com'
export const CDN_ROOT_URL = process.env.NEXT_PUBLIC_CDN_URL || 'https://cdn.ordinalswallet.com'
export const ORD_ROOT_URL = process.env.NEXT_PUBLIC_ORD_URL || 'https://ord-lb.ordinalswallet.com'
export const ORD_CONTENT_URL = `${ORD_ROOT_URL}/content`
export const CDN_URL = process.env.NEXT_PUBLIC_ORD_URL
	? ORD_CONTENT_URL
	: `${CDN_ROOT_URL}/inscription/content`
export const CDN_PREVIEW_URL = `${CDN_ROOT_URL}/inscription/preview`
// Recursive HTML/SVG inscriptions reference `/content/<id>` and `/r/...`
// relative to the iframe origin. Our CDN serves only `/inscription/content/`,
// so those relative paths 404 and the inscription renders blank.
//
// Default to Unisat's mirror — `static.unisat.io` is an ord-server clone that
// (a) serves the same `/content/<id>` paths, (b) rewrites recursive references
// inside HTML/SVG bodies to absolute URLs before serving, and (c) sets a
// permissive CSP so recursive sub-fetches resolve. No proxy code on our side.
//
// Override with `NEXT_PUBLIC_RECURSIVE_CONTENT_URL` in Vercel project env if
// we ever need to point at a different mirror or our own proxy.
export const RECURSIVE_CONTENT_URL =
	process.env.NEXT_PUBLIC_RECURSIVE_CONTENT_URL || 'https://static.unisat.io/content'

// Server-side recursive-SVG rasterizer on turbo. Returns a single PNG so SVG
// inscriptions (incl. recursive composites like Bitcoin Weirdos, xFrogs,
// Pizza Ninjas static layer) can render via a plain `<img>` instead of a
// sandboxed iframe — kills the sandbox/postMessage/storage console errors,
// eliminates an entire round-trip storm of recursive fetches per card, and
// drops bytes-over-the-wire ~10x for the grid views.
//
// HTML inscriptions are NOT covered by /render — those still go through
// RECURSIVE_CONTENT_URL into an iframe (JS/canvas/WebGL needs a browser).
export const RENDER_URL =
	process.env.NEXT_PUBLIC_RENDER_URL || 'https://turbo.ordinalswallet.com/render'
export const RECURSIVE_RENDERER_URL =
	process.env.NEXT_PUBLIC_RECURSIVE_RENDERER_URL ||
	'https://renderer.ordinalswallet.com/api/inscription'
export const RECURSIVE_RENDERER_VERSION =
	process.env.NEXT_PUBLIC_RECURSIVE_RENDERER_VERSION || '20260605'
export const RECURSIVE_RENDERER_IMAGE_SIZE = 512
// HTML collections whose layout only shows the whole piece on a desktop-width
// viewport. The renderer's viewport is the screenshot size, and at 512 these
// stack their side panel under the art, so grid cards showed a cropped piece.
// 1024 is the renderer's maximum and clears their breakpoint.
export const FULL_LAYOUT_RENDER_COLLECTION_SLUGS: Record<string, number> = {
	flyvision: 1024,
}
export const NAT_OWNERS_API_URL = process.env.NEXT_PUBLIC_NAT_OWNERS_API_URL || ''
export const DISCORD_INVITE = 'https://discord.gg/ordinalswallet'
export const GITHUB_URL = 'https://github.com/ordinals-wallet/ordinals-collections'
// MEMPOOL_DOT_SPACE is the legacy name (some callsites pre-date the rename).
// EXPLORER_URL is the canonical name. Both alias the same value; don't add a
// new export — pick EXPLORER_URL in new code.
export const EXPLORER_URL = process.env.NEXT_PUBLIC_EXPLORER_URL || 'https://mempool.space'
export const ESPO_URL = 'https://espo.sh'
export const MEMPOOL_DOT_SPACE = EXPLORER_URL
export const MEDIA_URL = 'https://media.ordinalswallet.com'
export const COLLECTIONS_VERSION = 'v2/collections'
export const SEARCH_VERSION = 'v2/search'

// export const COLLECTIONS_VERSION = 'collections'
// export const SEARCH_VERSION = 'search'

export const FIREBASE_CF_SAVE_TOKEN_API_URL = 'https://savetoken-6tgpa7serq-uc.a.run.app'
export const FIREBASE_CF_GET_TOKEN_STATUS_API_URL = 'https://gettokenstatus-6tgpa7serq-uc.a.run.app'
export const FIREBASE_CF_REMOVE_TOKEN_API_URL = 'https://removetoken-6tgpa7serq-uc.a.run.app'

export const STORAGE_ENCRYPTED_SEED = 'ordinals_wallet::encrypted_seed'
export const STORAGE_ENCRYPTED_SEED_SALT = 'ordinals_wallet::encrypted_seed_salt'
export const STORAGE_ENCRYPTED_SEED_TYPE = 'ordinals_wallet::encrypted_seed_type'
export const STORAGE_LAST_ACTIVE_AT = 'ordinals_wallet::last_active_at'
export const STORAGE_PUBLIC_KEY = 'ordinals_wallet::public_key'
export const STORAGE_TAPROOT_ADDRESS = 'ordinals_wallet::taproot_address'
export const STORAGE_ACK_WALLET_WARNING = 'ordinals_wallet::ack_wallet_warning'
export const STORAGE_DID_REGISTER = 'ordinals_wallet::did_register'
export const STORAGE_WALLET_TYPE = 'ordinals_wallet::wallet_type'
export const STORAGE_XVERSE_PAYMENT_ADDRESS = 'ordinals_wallet::xverse_payment_address'
export const STORAGE_XVERSE_TAPROOT_PUBLIC_KEY = 'ordinals_wallet::xverse_taproot_public_key'
export const STORAGE_FLOATING_MENU = 'ordinals_wallet::floating_menu'
export const STORAGE_PROFILE = 'ordinals_wallet::profile'
export const STORAGE_QUIT = 'ordinals_wallet::quit-password-modal'
/** Legacy dummy-PSBT credential. Only read to clear it; see utils/wallet/session. */
// oxlint-disable-next-line react-doctor/no-secrets-in-client-code -- localStorage key, not a secret
export const STORAGE_AUTH_PSBT = 'ordinals_wallet::auth_psbt'
// oxlint-disable-next-line react-doctor/no-secrets-in-client-code -- localStorage key, not a secret
export const STORAGE_AUTH_SESSION = 'ordinals_wallet::auth_session'

export const MOBILE_BREAKPOINT = 736
export const TABLET_BREAKPOINT = 1073
export const DESKTOP_BREAKPOINT = 1280
export const EMAIL_REGEX = /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

export const MINT_ALERTS = 'h7G763S0UwEsKtHOjqF892RXGg'
export const LAUNCH_ALERTS = 'drH1nv5wIVP8j1ZYSb9usg'

export const BEST_IN_SLOT_API = 'https://brc20api.bestinslot.xyz/v1'
export const BEST_IN_SLOT = 'https://bestinslot.xyz'
export const COLLECTION_SLUGS = [
	'bitcoin-rocks',
	'rare-scrilla-sounds',
	'ordinal-diamonds',
	'ordinal-rabbits',
	'bible-ordinals',
	'squiggs',
	'ordinal-tortoises',
	'the-fiat-collection',
	'btc-machines',
	'h17n-text',
	'bureaucrats',
	'sharkies'
]

export const COLLECTION_IMAGE_OVERRIDES: Record<string, string> = {
	cents: '/collections/cents/icon.jpg',
	omb: 'https://media.ordinalswallet.com/85c9f721e8080065eb769c05f9fdbf17ba41dbb5dd709663fd36d78bbd996c88.avif'
}

export const NAMES_SLUGS = [
	'sat-names',
	'unisat-names',
	'xbt-names',
	'btc-names',
	'gm-names',
	'gua-names',
	'meta-names',
	'ow-names',
	'pepe-names',
	'mllt-names',
	'ord-names',
	'magic-names',
	'satoria-names',
	'x-names',
	'orange-names',
	'bitter-names',
	'ord-names',
	'𝕏-names',
	'doge-names',
	'based-names',
	'kek-names',
	'dickbutt-names'
]

export const TEXT_COLLECTION_SLUGS = NAMES_SLUGS.concat(['bitmap'])

export const REMOTE_SLUGS = [
	'sat-names',
	'btc-survival-gear',
	'btc-names',
	'sub-100k',
	'btc-artifacts',
	'unisat-names',
	'bitmap',
	'natmap'
]

export const DEFAULT_FEED_FILTER = 'VolumeWeekDesc'

export const FILTER_COLLECTIONS_DEFAULT_ID = 'filterCollections'

export const TOOL_FEE_ADDRESS = 'bc1q4ptn03hgaam2qw7fhjq88wp6d390j2gzndux0e'
export const MARKET_ROYALTY_ADDRESS =
	'bc1p6yd49679azsaxqgtr52ff6jjvj2wv5dlaqwhaxarkamevgle2jaqs8vlnr'
export const MARKET_FEE_PERCENTAGE = 2.7
export const MARKET_FEE_RATE = MARKET_FEE_PERCENTAGE / 100

type TapBalanceTransport = 'rest' | 'ws'

export const readTapBalanceTransport = (): TapBalanceTransport => {
	return process.env.NEXT_PUBLIC_TAP_BALANCE_TRANSPORT === 'ws' ? 'ws' : 'rest'
}
